import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import { Pin, Calendar, ArrowLeft, Loader2, AlertCircle } from "lucide-react";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store";
import Header from "../../../components/common/Header";
import Footer from "../../../components/common/Footer";
import BackToTop from "../../../components/common/BackToTop";
import { useAnnouncement } from "../../../services/announcementService";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";

const AnnouncementDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const {
    data: announcement,
    isLoading,
    error,
  } = useAnnouncement(parseInt(id || "0"), !!id);

  // Trigger animations when data loads
  useEffect(() => {
    if (!isLoading && announcement) {
      setIsDataLoaded(true);
    }
  }, [isLoading, announcement]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString(currentLanguage, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <Header />
        <main className="py-16 px-4 sm:px-16">
          <div className="max-w-4xl mx-auto text-center">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-lg text-gray-600 dark:text-gray-400">
              {t("announcements.loading", "Loading announcement details...")}
            </p>
          </div>
        </main>
        <Footer />
        <BackToTop />
      </div>
    );
  }

  if (error || !announcement) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <Header />
        <main className="py-16 px-4 sm:px-16">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-white dark:bg-gray-800 border-red-200 dark:border-red-900">
              <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
                <AlertCircle className="w-12 h-12 text-red-500" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t("announcements.error", "Failed to load announcement")}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {error instanceof Error ? error.message : "Unknown error"}
                </p>
                <Button
                  onClick={() => navigate(`/${currentLanguage}/announcements`)}
                  variant="outline"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t("common.back", "Back to Announcements")}
                </Button>
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
        <BackToTop />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <Header />
      <main className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={isDataLoaded ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="mb-6"
          >
            <Button
              onClick={() => navigate(`/${currentLanguage}/announcements`)}
              variant="outline"
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("common.back", "Back to Announcements")}
            </Button>
          </motion.div>

          {/* Announcement Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isDataLoaded ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-xl overflow-hidden">
              {announcement.is_pinned && (
                <div className="h-2 bg-gradient-to-r from-red-500 to-pink-500" />
              )}
              <CardContent className="p-8">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 mb-6">
                  <div className="flex-1">
                    {announcement.is_pinned && (
                      <div className="flex items-center gap-2 text-red-600 dark:text-red-400 mb-3">
                        <Pin className="w-4 h-4" />
                        <span className="text-sm font-semibold">
                          {t("announcements.pinned", "Pinned Announcement")}
                        </span>
                      </div>
                    )}
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                      {announcement.title}
                    </h1>
                    {announcement.created_at && (
                      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(announcement.created_at)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Body */}
                <div className="prose prose-lg dark:prose-invert max-w-none">
                  <div
                    className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{
                      __html: announcement.body.replace(/\n/g, "<br />"),
                    }}
                  />
                </div>

                {/* Footer */}
                {(announcement.visible_from || announcement.visible_to) && (
                  <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-400">
                      {announcement.visible_from && (
                        <div>
                          <span className="font-semibold">
                            {t("admin.announcements.visibleFrom", "Visible From")}:
                          </span>{" "}
                          {formatDate(announcement.visible_from)}
                        </div>
                      )}
                      {announcement.visible_to && (
                        <div>
                          <span className="font-semibold">
                            {t("admin.announcements.visibleTo", "Visible To")}:
                          </span>{" "}
                          {formatDate(announcement.visible_to)}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

export default AnnouncementDetail;

